import { ProductInfo } from "@insite/client-framework/Common/ProductInfo";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { CategoryContext } from "@insite/client-framework/Components/CategoryContext";
import { HasShellContext, withIsInShell } from "@insite/client-framework/Components/IsInShell";
import { ProductContext } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import {
    getErrorMessage,
    getProductsForProductInfoList,
} from "@insite/client-framework/Store/Components/ProductInfoList/ProductInfoListSelectors";
import { BrandStateContext } from "@insite/client-framework/Store/Data/Brands/BrandsSelectors";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import SkipNav, { SkipNavStyles } from "@insite/content-library/Components/SkipNav";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import getProductInfoListStatus from "@MasterBluePrint/Components/CustomProductInfoListSelector";
import LazyLoadWrapper from "@MasterBluePrint/Components/LazyLoadWrapper";
import loadCarouselProducts from "@MasterBluePrint/Handlers/LoadCarouselProductsHandler";
import logProductImpression from "@MasterBluePrint/Handlers/ProductImpressionTrackingHandler";
import ProductCarouselProduct, {
    ProductCarouselProductStyles,
} from "@MasterBluePrint/Overrides/Components/ProductCarouselProduct";
import { CustomApplicationState } from "@MasterBluePrint/Store/CustomApplicationState";
import themeColors from "@MasterBluePrint/Styles/variables";
import useEmblaCarousel from "embla-carousel-react";
import React, { createRef, useCallback, useContext, useEffect, useRef, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css, ThemeProps, withTheme } from "styled-components";

const enum fields {
    title = "title",
    carouselType = "carouselType",
    displayProductsFrom = "displayProductsFrom",
    selectedCategoryIds = "selectedCategoryIds",
    relatedProductType = "relatedProductType",
    numberOfProductsToDisplay = "numberOfProductsToDisplay",
    seedWithManuallyAssigned = "seedWithManuallyAssigned",
    maxNumberOfColumns = "maxNumberOfColumns",
    showImage = "showImage",
    showTitle = "showTitle",
    showBrandName = "showBrandName",
    showPartNumbers = "showPartNumbers",
    showPrice = "showPrice",
    showBreakPrice = "showBreakPrice",
    showAddToCart = "showAddToCart",
    showAddToList = "showAddToList",
    hideProductPriceSavingsMessage = "hideProductPriceSavingsMessage",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.carouselType]: string;
        [fields.displayProductsFrom]: string;
        [fields.selectedCategoryIds]: string[];
        [fields.relatedProductType]: string;
        [fields.numberOfProductsToDisplay]: number;
        [fields.seedWithManuallyAssigned]: string;
        [fields.maxNumberOfColumns]: number;
        [fields.showImage]: boolean;
        [fields.showTitle]: boolean;
        [fields.showBrandName]: boolean;
        [fields.showPartNumbers]: boolean;
        [fields.showPrice]: boolean;
        [fields.showBreakPrice]: boolean;
        [fields.showAddToCart]: boolean;
        [fields.showAddToList]: boolean;
        [fields.hideProductPriceSavingsMessage]: boolean;
    };
}

const mapStateToProps = (state: CustomApplicationState, ownProps: OwnProps) => {
    const mapId = state.components.productInfoLists.dataMapping[ownProps.id];
    return {
        products: getProductsForProductInfoList(state, ownProps.id),
        errorMessage: getErrorMessage(state, ownProps.id),
        pageType: getCurrentPage(state).type,
        productInfoListStatus: getProductInfoListStatus(state, ownProps.id),
        productInfoById: state.components.productInfoLists.productInfoListById[mapId || ownProps.id],
        state
    };
};

const mapDispatchToProps = {
    loadCarouselProducts,
    logProductImpression,
};

type Props = OwnProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    ThemeProps<BaseTheme> &
    HasShellContext;

export interface ProductCarouselStyles {
    errorMessageText?: TypographyPresentationProps;
    titleText?: TypographyPresentationProps;
    skipCarouselButton?: SkipNavStyles;
    mainContainer?: GridContainerProps;
    prevArrowGridItem?: GridItemProps;
    prevArrowButton?: ButtonPresentationProps;
    nextArrowGridItem?: GridItemProps;
    slidesNumberGridItem?: GridItemProps;
    nextArrowButton?: ButtonPresentationProps;
    carouselGridItem?: GridItemProps;
    carouselWrapper?: InjectableCss;
    carouselContainer?: InjectableCss;
    carouselSlidesContainer?: InjectableCss;
    carouselSlide?: InjectableCss;
    carouselSlideInner?: InjectableCss;
    carouselProductStyles?: ProductCarouselProductStyles;
    sliderHeader?: InjectableCss;
    sliderArrows?: InjectableCss;
}

export const productCarouselStyles: ProductCarouselStyles = {
    sliderHeader: {
        css: css`
            display: flex;
            justify-content: space-between;
            width: 100%;
            border-width: 1.25rem;
            border-bottom: 1px solid #C2D1D9;
            padding-bottom: .625rem;
            margin-bottom: 1.5625rem;
            margin-left: .625rem;
            margin-right: .625rem;
            @media (max-width: 991px) {
                padding-left: 0;
                padding-bottom: 0;
            }
            @media (max-width: 768px) {
                margin-left: .9rem;
            }
        `,
    },
    sliderArrows: {
        css: css`
            display: flex;
            gap: 0;
            justify-content: end;
            @media (max-width: 991px) {
                justify-content: left;
            }
        `,
    },
    errorMessageText: {
        color: "danger",
        css: css`
            margin-top: 1.25rem;
        `,
    },
    titleText: {
        variant: "h2",
        css: css`
            margin-bottom: 1rem;
            text-align: center;
            margin-bottom: 0;
            color: ${themeColors.colorPrimary};
            font-size: 1.25rem;
            font-weight: 500;
            letter-spacing: 0;
            line-height: 2rem;
        `,
    },
    mainContainer: {
        gap: 0,
        css: css`
            margin: 0 1.25rem;
            background: ${themeColors.colorWhite};
            padding: 2.5rem 3.75rem 2.5rem 2.5rem;
            position: relative;
            width: auto;
            border: 1px solid rgb(0,37,84,0.08);
            
            @media screen and (max-width: 1199px) and (min-width: 992px) {
                width: 94%;
                padding: 2.5rem .9375rem 2.5rem .9375rem;
            }
            @media (max-width: 991px) {
                margin: 0;
                padding: .9375rem 0;
                max-width: 100%;
                width: 100%;
                > div {
                    width: 100%;
                }
            }
            span.stock-container {
                min-height: 43px;
                margin-top: 10px;
            }
            div.price-wrapper {
                display: none;
            } 
        `,
    },
    prevArrowGridItem: {
        width: 1,
        align: "middle",
        css: css`
            justify-content: flex-start;
            flex-basis: 100%;
            max-width: 100%;
            &:hover {
                border:none;
                background:none;
            }
        `,
    },
    prevArrowButton: {
        variant: "secondary",
        color: "text.main",
        css: css`
            border: 0;
            padding: 0;
            .KWUcc svg path {
                fill: #2060AD;
            }
            &:hover {
                border:none !important;
                background: none !important;
            }
        `,
    },
    slidesNumberGridItem: {
        align: "middle",
        css: css`
            justify-content: flex-end;
            flex-basis: 100%;
            max-width: 100%;
            min-width: 100px;
            justify-content: center;
            font-family: 'Lato', sans-serif;
            color: #333F48;
            font-size: 1rem;

            span {
                font-family: 'Lato',sans-serif;
                color: #333F48;
                font-size: 1rem;
            }
        `,
    },
    nextArrowGridItem: {
        width: 1,
        align: "middle",
        css: css`
            justify-content: flex-end;
            flex-basis: 100%;
            max-width: 100%;
        `,
    },
    nextArrowButton: {
        variant: "secondary",
        color: "text.main",
        css: css`
            border: 0;
            padding: 0;
            .KWUcc svg path {
                fill: #2060AD;
            }
            &:hover {
                border:none !important;
                background: none !important;
            }
        `,
    },
    carouselGridItem: {
        width: 10,
        css: css`
            
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        flex-basis: 90%;
                        max-width: 90%;
                    `,
                    css`
                        flex-basis: 90%;
                        max-width: 90%;
                    `,
                    css`
                        flex-basis: 95%;
                        max-width: 95%;
                    `,
                    css`
                        flex-basis: 100%;
                        max-width: 100%;
                    `,
                    css`
                        flex-basis: 100%;
                        max-width: 100%;
                    `,
                ])}
        `,
    },
    carouselWrapper: {
        css: css`
            width: 100%;
            position: relative;
        `,
    },
    carouselContainer: {
        css: css`
            overflow: hidden;
        `,
    },
    carouselSlidesContainer: {
        css: css`
            display: flex;
        `,
    },
    carouselSlide: {
        css: css`
            padding: 0;
            position: relative;
            flex: 0 0 auto;
            min-height: initial !important;
        `,
    },
    carouselSlideInner: {
        css: css`
            width: 100%;
            height: 100%;
            position: relative;
            padding: .75rem;
            display: flex;
            flex-direction: column;

            div[class^="InventoryMessage"],
            div[class*=" InventoryMessage"] {
                margin-top: 0 !important;

                &.ship-manufacture-align {
                    margin-top: 12px !important;
                }
            }
            
            .btn-present {
                padding-bottom: 10px;
                height: 100%;

                .adjust-spacing {
                    max-width: 100%;
                }
            }

            .vat-field {
                margin-top: .5rem;
            }

            .btn-not-present {
                padding-bottom: 20px !important;
            }

            @media screen and (max-width: 1199px) and (min-width: 992px) {
                padding: 0 .75rem;
            }
        `,
    },
};

const styles = productCarouselStyles;

const ProductCarousel: React.FC<Props> = ({
    id,
    theme,
    fields,
    products,
    errorMessage,
    loadCarouselProducts,
    pageType,
    shellContext,
    logProductImpression,
    state,
    productInfoListStatus,
    productInfoById,
}) => {
    const afterCarousel = createRef<HTMLSpanElement>();
    const isProductDetailsPage = pageType === "ProductDetailsPage";
    const isProductListPage = pageType === "ProductListPage";
    const isBrandDetailsPage = pageType === "BrandDetailsPage";
    const brand = useContext(BrandStateContext).value;
    const category = useContext(CategoryContext);
    const productContext = useContext(ProductContext);
    const [emblaRef, embla] = useEmblaCarousel();
    const [windowResizeTime, setWindowResizeTime] = useState(0);
    const [canScrollPrev, setCanScrollPrev] = useState(false);
    const [canScrollNext, setCanScrollNext] = useState(false);
    const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
    const [totalSlides, setTotalSlides] = useState(products?.length);
    const [isCarouselVisible, setIsCarouselVisible] = useState(false);

    const setCanScroll = () => {
        if (shellContext.isInShell) {
            return;
        }
        setCanScrollPrev(!!embla && embla.canScrollPrev());
        setCanScrollNext(!!embla && embla.canScrollNext());
        if (embla) {
            const selectedScrollSnap = embla.selectedScrollSnap();
            setCurrentSlideIndex(selectedScrollSnap);
            setTotalSlides(embla.scrollSnapList().length);
        }
    };

    const getSlidesToScroll = useCallback(() => {
        const maxNumberOfColumns = fields.maxNumberOfColumns || 4;
        if (typeof window === "undefined") {
            return maxNumberOfColumns;
        }

        let localSlidesToScroll: number;
        if (window.innerWidth < theme.breakpoints.values[1]) {
            localSlidesToScroll = 2;
        } else if (window.innerWidth < theme.breakpoints.values[2]) {
            localSlidesToScroll = 2;
        } else if (window.innerWidth < theme.breakpoints.values[3]) {
            localSlidesToScroll = 3;
        } else if (window.innerWidth < theme.breakpoints.values[4]) {
            localSlidesToScroll = 4;
        } else {
            localSlidesToScroll = 5;
        }
        return Math.min(localSlidesToScroll, maxNumberOfColumns);
    }, [fields.maxNumberOfColumns]);

    const [slidesToScroll, setSlidesToScroll] = useState(getSlidesToScroll());

    const getDraggable = () => {
        if (typeof window === "undefined") {
            return false;
        }

        return window.innerWidth < theme.breakpoints.values[1];
    };

    const [draggable, setDraggable] = useState(getDraggable());

    const hasRunOnce = useRef(false);
    useEffect(() => {
        if (hasRunOnce.current) return;

        if (productInfoListStatus?.isRealTimeInventoryLoaded 
            && productInfoListStatus.isRealTimePricingLoaded
            && products && products.length > 0) 
        {
            hasRunOnce.current = true;
            const productInfoList = productInfoById ? Object.values(productInfoById.productInfoByProductId).filter((p): p is ProductInfo => p !== undefined): [];
            logProductImpression({ productCollection: products, listType: translate(carouselTypeOptions.find(o => o.value === fields.carouselType)?.displayName || "Product Carousel"), carouselTitle:fields.title, productInfoCollection: productInfoList});
        }
    }, [productInfoListStatus?.isRealTimeInventoryLoaded, productInfoListStatus?.isRealTimePricingLoaded]);

    useEffect(() => {
        loadCarouselProducts({
            carouselId: id,
            carouselType: fields.carouselType,
            relatedProductType: fields.relatedProductType,
            seedWithManuallyAssigned: fields.seedWithManuallyAssigned,
            displayProductsFrom: fields.displayProductsFrom,
            selectedCategoryIds: fields.selectedCategoryIds,
            numberOfProductsToDisplay: fields.numberOfProductsToDisplay,
            isProductDetailsPage,
            productId: productContext?.product?.id,
            isProductListPage,
            category,
            isBrandDetailsPage,
            brand,
        });
    }, [
        fields.carouselType,
        fields.relatedProductType,
        fields.seedWithManuallyAssigned,
        fields.displayProductsFrom,
        fields.selectedCategoryIds,
        fields.numberOfProductsToDisplay,
        productContext?.product?.id,
        category?.id,
        brand?.id
    ]);

    useEffect(() => {
        const newSlidesToScroll = getSlidesToScroll();
        setSlidesToScroll(newSlidesToScroll);

        const newDraggable = getDraggable();
        setDraggable(newDraggable);
    }, [fields.maxNumberOfColumns, windowResizeTime]);

    useEffect(() => {
        if (!embla) {
            return;
        }

        embla.on("init", setCanScroll);
        embla.on("select", setCanScroll);

        const onWindowResize = () => {
            setWindowResizeTime(Date.now());
        };

        if (typeof window !== 'undefined') {
            window.addEventListener("resize", onWindowResize);
            onWindowResize();
        }

        return () => {
            if (typeof window !== 'undefined') {
                window.removeEventListener("resize", onWindowResize);
            }
        };
    }, [embla]);

    useEffect(() => {
        if (!embla || !products || products.length === 0) {
            return;
        }

        embla.reInit({
            align: "start",
            slidesToScroll,
            watchDrag: draggable,
            loop: products.length > slidesToScroll,
        });
        setCanScroll();
    }, [embla, slidesToScroll, draggable, products]);

    if (errorMessage && shellContext.isInShell) {
        return (
            <Typography {...styles.errorMessageText} as="p">
                {`Failed to load carousel products. Error: '${errorMessage}'. Please check widget settings.`}
            </Typography>
        );
    }
    if (!products || products.length === 0) {
        return null;
    }

    const title =
        fields.title ||
        translate(carouselTypeOptions.find(o => o.value === fields.carouselType)?.displayName || "Product Carousel");
    const showBrandBlocks = fields.showBrandName && products.some(o => !!o.brand);


    const slides = document.querySelectorAll<HTMLElement>('[data-test-selector="slides"]');
    const prevButton = document.querySelector<HTMLElement>('[data-test-selector="prevBtn"]');
    const nextButton = document.querySelector<HTMLElement>('[data-test-selector="nextBtn"]');
    const sliderDraggable = document.querySelector<HTMLElement>('.is-draggable');
    function setMinHeightAndSpacing() {
        setTimeout(() => {
            slides.forEach(slide => {
                const priceSections = slide.querySelectorAll<HTMLElement>('.is-selected .price-section');
                const adjustSpacings = slide.querySelectorAll<HTMLElement>('.is-selected .adjust-spacing');
                const productDetailLinks = slide.querySelectorAll<HTMLElement>('.is-selected .product-detail-link');
                const mfgContainers = slide.querySelectorAll<HTMLElement>('.is-selected .mfg-container');
                let maxHeightPriceContainer = 0;
                let maxHeightMsgContainer = 0;
                let maxHeightPDLContainer = 0;
                let maxHeightMFGContainer = 0;


                // Clear previously set min heights
                priceSections.forEach(priceSection => {
                    (priceSection as HTMLElement).style.minHeight = '';
                });

                adjustSpacings.forEach(adjustSpacing => {
                    (adjustSpacing as HTMLElement).style.minHeight = '';
                });

                productDetailLinks.forEach(productDetailLink => {
                    (productDetailLink as HTMLElement).style.minHeight = '';
                });

                mfgContainers.forEach(mfgContainer => {
                    (mfgContainer as HTMLElement).style.minHeight = '';
                });

                // Find the maximum height among priceSections and adjustSpacings
                priceSections.forEach(priceSection => {
                    const currentHeightPS = (priceSection as HTMLElement).offsetHeight;

                    if (currentHeightPS > maxHeightPriceContainer) {
                        maxHeightPriceContainer = currentHeightPS;
                    }
                });

                adjustSpacings.forEach(adjustSpacing => {
                    const currentHeightMC = (adjustSpacing as HTMLElement).offsetHeight;

                    if (currentHeightMC > maxHeightMsgContainer) {
                        maxHeightMsgContainer = currentHeightMC;
                    }
                });

                productDetailLinks.forEach(productDetailLink => {
                    const currentHeightPD = (productDetailLink as HTMLElement).offsetHeight;

                    if (currentHeightPD > maxHeightPDLContainer) {
                        maxHeightPDLContainer = currentHeightPD;
                    }
                });

                mfgContainers.forEach(mfgContainer => {
                    const currentHeightMFG = (mfgContainer as HTMLElement).offsetHeight;

                    if (currentHeightMFG > maxHeightMFGContainer) {
                        maxHeightMFGContainer = currentHeightMFG;
                    }
                });

                // Set the minimum height for adjustSpacings and priceSections
                priceSections.forEach(priceSection => {
                    (priceSection as HTMLElement).style.minHeight = `${maxHeightPriceContainer}px`;
                });

                adjustSpacings.forEach(adjustSpacing => {
                    (adjustSpacing as HTMLElement).style.minHeight = `${maxHeightMsgContainer}px`;
                });

                productDetailLinks.forEach(productDetailLink => {
                    (productDetailLink as HTMLElement).style.minHeight = `${maxHeightPDLContainer}px`;
                });

                mfgContainers.forEach(mfgContainer => {
                    (mfgContainer as HTMLElement).style.minHeight = `${maxHeightMFGContainer}px`;
                });
            });
        }, 700);
    }

    function handlePrevButtonClick() {
        if (embla) {
            setMinHeightAndSpacing();
            setCurrentSlideIndex(embla.selectedScrollSnap());
        }
    }

    function handleNextButtonClick() {
        if (embla) {
            setMinHeightAndSpacing();
            setCurrentSlideIndex(embla.selectedScrollSnap());
        }
    }

    function handleDrag() {
        setMinHeightAndSpacing();
    }

    setMinHeightAndSpacing();

    if (typeof window !== 'undefined') {
        window.addEventListener('resize', setMinHeightAndSpacing);
    }

    if (sliderDraggable) {
        if (typeof window !== 'undefined') {
            if (window.innerWidth < 768) {
                sliderDraggable.addEventListener('touchend', handleDrag);
            }
        }
    }

    if (prevButton) {
        prevButton.addEventListener('click', handlePrevButtonClick);
    }

    if (nextButton) {
        nextButton.addEventListener('click', handleNextButtonClick);
    }

    return (
        <LazyLoadWrapper setIsVisible={setIsCarouselVisible}>
            <SkipNav
                text={translate("Skip Carousel")}
                extendedStyles={styles.skipCarouselButton}
                destination={afterCarousel}
            />
            <GridContainer
                {...styles.mainContainer}
                data-test-selector={`productCarousel_${fields.carouselType}${fields.relatedProductType}`}
                className="product-carousel"
            >
                <StyledWrapper {...styles.sliderHeader} className="slide-header">
                    <Typography {...styles.titleText}>{title}</Typography>

                    <StyledWrapper {...styles.sliderArrows}>
                        {products.length > slidesToScroll && (
                            <GridItem {...styles.prevArrowGridItem}>
                                <Button
                                    {...styles.prevArrowButton}
                                    onClick={() => embla && embla.scrollPrev()}
                                    disabled={!canScrollPrev}
                                    data-test-selector="prevBtn"
                                    aria-label="Previous featured products group"
                                >
                                    <span>
                                        <svg width="19" height="14" xmlns="http://www.w3.org/2000/svg">
                                            <path
                                                d="M7.41 1.41 2.47 6.35H18.2v1.3H2.47l4.94 4.94-.91.91L0 7 6.5.5z"
                                                fill="#2060AD"
                                                fillRule="evenodd"
                                            />
                                        </svg>
                                    </span>
                                </Button>
                            </GridItem>
                        )}

                        <GridItem {...styles.slidesNumberGridItem}>
                            <Typography>{`${currentSlideIndex + 1} of ${totalSlides}`}</Typography>
                        </GridItem>
                        <GridItem {...styles.nextArrowGridItem}>
                            {products.length > slidesToScroll && (
                                <Button
                                    {...styles.nextArrowButton}
                                    onClick={() => embla && embla.scrollNext()}
                                    disabled={!canScrollNext}
                                    data-test-selector="nextBtn"
                                    aria-label="Next featured products group"
                                >
                                    <span>
                                        <svg width="19" height="14" xmlns="http://www.w3.org/2000/svg">
                                            <path
                                                d="m10.79 1.41 4.94 4.94H0v1.3h15.73l-4.94 4.94.91.91L18.2 7 11.7.5z"
                                                fill="#2060AD"
                                                fillRule="evenodd"
                                            />
                                        </svg>
                                    </span>
                                </Button>
                            )}
                        </GridItem>
                    </StyledWrapper>
                </StyledWrapper>

                <GridItem {...styles.carouselGridItem}>
                    <StyledWrapper {...styles.carouselWrapper}>
                        <StyledWrapper {...styles.carouselContainer} ref={emblaRef}>
                            <StyledWrapper {...styles.carouselSlidesContainer} data-test-selector="slides">
                                {products.map(product => (
                                    <StyledWrapper
                                        {...styles.carouselSlide}
                                        key={product.id}
                                        style={{ width: `calc(100% / ${slidesToScroll})` }}
                                    >
                                        <StyledWrapper
                                            {...styles.carouselSlideInner}
                                            id={`product_${product.id}`}
                                            data-test-selector="productContainer"
                                        >
                                            <ProductCarouselProduct
                                                carouselId={id}
                                                product={product}
                                                showImage={fields.showImage}
                                                showBrand={showBrandBlocks}
                                                showTitle={fields.showTitle}
                                                showPartNumbers={fields.showPartNumbers}
                                                showPrice={fields.showPrice}
                                                showBreakPrice={fields.showBreakPrice}
                                                showAddToCart={fields.showAddToCart}
                                                showAddToList={fields.showAddToList}
                                                extendedStyles={styles.carouselProductStyles}
                                                carouselType={
                                                    carouselTypeOptions.find(o => o.value === fields.carouselType)
                                                        ?.displayName || "Product Carousel"
                                                }
                                                index={products.findIndex(e => e.id === product.id)}
                                                relatedProductType={fields.relatedProductType}
                                                hideProductPriceSavingsMessage={fields.hideProductPriceSavingsMessage}
                                                carouselTitle={title}
                                                isCarouselProduct={true}
                                            />
                                        </StyledWrapper>
                                    </StyledWrapper>
                                ))}
                            </StyledWrapper>
                        </StyledWrapper>
                    </StyledWrapper>
                </GridItem>
            </GridContainer>
            <span ref={afterCarousel} tabIndex={-1} />
        </LazyLoadWrapper>
    );
};

const carouselTypeOptions = [
    { value: "crossSells", displayName: "Cross Sells" },
    { value: "customersAlsoPurchased", displayName: "Customers Also Purchased" },
    { value: "recentlyViewed", displayName: "Recently Viewed" },
    { value: "relatedProducts", displayName: "Related Products" },
    { value: "topSellers", displayName: "Top Sellers" },
];

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withIsInShell(withTheme(ProductCarousel))),
    definition: {
        group: "Common",
        icon: "Carousel",
        fieldDefinitions: [
            {
                name: fields.title,
                displayName: "Title",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
                sortOrder: 0,
            },
            {
                name: fields.carouselType,
                displayName: "Carousel Type",
                editorTemplate: "DropDownField",
                options: carouselTypeOptions,
                defaultValue: "crossSells",
                fieldType: "General",
                sortOrder: 1,
                customFilter: (item, page, settings) => {
                    return (
                        settings.settingsCollection.websiteSettings.enableDynamicRecommendations ||
                        item.value !== "topSellers"
                    );
                },
            },
            {
                name: fields.displayProductsFrom,
                displayName: "Display Products From",
                editorTemplate: "DropDownField",
                options: [
                    { value: "allCategories", displayName: "All Categories" },
                    { value: "selectedCategories", displayName: "Selected Categories" },
                    { value: "customerSegments", displayName: "Customer Segments" },
                ],
                defaultValue: "allCategories",
                fieldType: "General",
                isVisible: item => item.fields[fields.carouselType] === "topSellers",
                sortOrder: 2,
            },
            {
                name: fields.selectedCategoryIds,
                displayName: "Selected Categories",
                editorTemplate: "CategoriesField",
                defaultValue: [],
                fieldType: "General",
                isVisible: item =>
                    item.fields[fields.carouselType] === "topSellers" &&
                    item.fields[fields.displayProductsFrom] === "selectedCategories",
                sortOrder: 3,
            },
            {
                name: fields.relatedProductType,
                displayName: "Related Product Type",
                editorTemplate: "SystemListDropDownField",
                systemListName: "ProductRelationship",
                defaultValue: "",
                fieldType: "General",
                isVisible: item => item.fields[fields.carouselType] === "relatedProducts",
                sortOrder: 4,
            },
            {
                name: fields.numberOfProductsToDisplay,
                displayName: "Number Of Products To Display",
                editorTemplate: "IntegerField",
                min: 1,
                max: 20,
                defaultValue: 10,
                fieldType: "General",
                isVisible: item =>
                    item.fields[fields.carouselType] === "customersAlsoPurchased" ||
                    item.fields[fields.carouselType] === "topSellers",
                sortOrder: 5,
            },
            {
                name: fields.seedWithManuallyAssigned,
                displayName: "Seed With Manually Assigned",
                editorTemplate: "SystemListDropDownField",
                systemListName: "ProductRelationship",
                defaultValue: "",
                fieldType: "General",
                isVisible: item => item.fields[fields.carouselType] === "customersAlsoPurchased",
                sortOrder: 6,
            },
            {
                name: fields.maxNumberOfColumns,
                displayName: "Max Number Of Columns",
                editorTemplate: "IntegerField",
                min: 1,
                max: 5,
                defaultValue: 5,
                fieldType: "General",
                sortOrder: 7,
            },
            {
                name: fields.showImage,
                displayName: "Image",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                isEnabled: () => false,
                fieldType: "General",
                sortOrder: 8,
            },
            {
                name: fields.showTitle,
                displayName: "Title",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                isEnabled: () => false,
                fieldType: "General",
                sortOrder: 9,
            },
            {
                name: fields.showBrandName,
                displayName: "Brand Name",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 10,
            },
            {
                name: fields.showPartNumbers,
                displayName: "Part Numbers",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 11,
            },
            {
                name: fields.showPrice,
                displayName: "Price",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 12,
            },
            {
                name: fields.showBreakPrice,
                displayName: "Break Price",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 13,
            },
            {
                name: fields.showAddToCart,
                displayName: "Add to Cart",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 14,
            },
            {
                name: fields.showAddToList,
                displayName: "Add to List",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 15,
            },
            {
                name: fields.hideProductPriceSavingsMessage,
                displayName: "Hide List Price",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 16,
            },
        ],
    },
};

export default widgetModule;